// PIN-gated reveal of a client's full SSN.
// Submits the agent's 6-digit PIN to POST /clients/:id/reveal-ssn. The
// backend validates the PIN (against the agent for AGENT/SOLO_AGENT/etc.,
// or against their supervisor for SEAT users), then decrypts the SSN
// with audit logging. The plaintext is shown briefly and auto-hidden,
// and is never persisted to React state any longer than necessary.
//
// Two modes:
//   - Default (no `onRevealed`): show the SSN inline with a 30-second
//     countdown, then auto-close. Used by the 🔒 SSN pill on the client
//     detail view.
//   - Callback mode (`onRevealed(ssn)` provided): hand the SSN back to
//     the caller (e.g. the SSN edit field) and close immediately. Caller
//     is responsible for whatever it does with the plaintext next.

import { useEffect, useRef, useState } from 'react';
import { Lock, ShieldCheck, ShieldAlert, X } from 'lucide-react';
import { Modal } from './ui.jsx';
import { revealClientSsn, updateClientSsn, verifyPin } from '../api.js';

const AUTO_HIDE_SECONDS = 30;

// Un asiento (assistant) NO tiene PIN propio: el servidor valida contra el PIN
// del agente que lo supervisa (ROLES_WITH_OWN_PIN en el backend). La ventana
// decía "tu PIN" a todo el mundo, así que un asiento escribía el suyo, recibía
// "PIN incorrecto" y no había forma de deducir de la pantalla que el PIN que
// hacía falta era el de otra persona.
function usesSupervisorPin() {
  try {
    return (typeof window !== 'undefined'
      && window.__deskteAuth?.currentUser?.role === 'assistant');
  } catch (_) {
    return false;
  }
}

// Qué PIN pide realmente la pantalla. Para un asiento es el del agente que lo
// supervisa, y decirlo ANTES evita el intento fallido en vez de explicarlo
// después (regla de oro 28: la pregunta se contesta en la pantalla).
export function pinOwnerLabel(es) {
  return usesSupervisorPin()
    ? (es ? 'el PIN de 6 dígitos del agente que supervisa tu cuenta' : 'your supervising agent’s 6-digit PIN')
    : (es ? 'tu PIN de 6 dígitos' : 'your 6-digit PIN');
}

// Un solo sitio arma el texto de CUALQUIER fallo de PIN — la ventana del SSN y
// la que salta al guardar la ficha comparten esta función. Antes cada una
// traducía dos códigos y dejaba los demás caer al mensaje crudo del servidor,
// en inglés: "Too many incorrect PIN attempts" y "No PIN owner: seat is not
// linked to a supervising agent" salían tal cual a una pantalla en español.
export function pinErrorText(e, es) {
  const code = e && e.code;
  const quedan = typeof e?.remainingAttempts === 'number' ? e.remainingAttempts : null;
  const supervisado = usesSupervisorPin();
  if (code === 'PIN_WRONG' || code === 'PIN_FORMAT') {
    const base = supervisado
      ? (es ? 'PIN incorrecto. Es el PIN del agente que supervisa tu cuenta, no el tuyo.'
            : 'Incorrect PIN. It is your supervising agent’s PIN, not your own.')
      : (es ? 'PIN incorrecto' : 'Incorrect PIN');
    // Decir cuántos intentos quedan ANTES del bloqueo. Los 15 minutos cierran
    // el SSN de TODOS los clientes, no solo el de esta ficha.
    if (quedan !== null && quedan > 0) {
      return base + (es
        ? ` — te queda${quedan === 1 ? '' : 'n'} ${quedan} intento${quedan === 1 ? '' : 's'} antes de que se bloquee 15 minutos.`
        : ` — ${quedan} attempt${quedan === 1 ? '' : 's'} left before a 15-minute lock.`);
    }
    return base;
  }
  if (code === 'PIN_LOCKED') {
    const min = typeof e?.retryAfterSec === 'number' ? Math.max(1, Math.ceil(e.retryAfterSec / 60)) : 15;
    return es
      ? `PIN bloqueado por demasiados intentos fallidos. Vuelve a intentarlo en ${min} minuto${min === 1 ? '' : 's'}. Mientras tanto no se puede ver ni guardar ningún SSN.`
      : `PIN locked after too many failed attempts. Try again in ${min} minute${min === 1 ? '' : 's'}. No SSN can be viewed or saved until then.`;
  }
  if (code === 'PIN_NOT_SET') {
    return supervisado
      ? (es ? 'El agente que supervisa tu cuenta todavía no ha configurado su PIN. Pídeselo: lo hace desde Mi Perfil → Seguridad.'
            : 'Your supervising agent has not set up their PIN yet. Ask them to set it in Profile → Security.')
      : (es ? 'No hay PIN configurado. Configúralo desde Mi Perfil → Seguridad.'
            : 'No PIN configured. Set one in Profile → Security.');
  }
  if (code === 'NO_PIN_OWNER') {
    return es
      ? 'Tu asiento no está enlazado a un agente supervisor, así que no hay ningún PIN contra el que validar. Avisa al dueño de la agencia.'
      : 'Your seat is not linked to a supervising agent, so there is no PIN to validate against. Tell the agency owner.';
  }
  return (e && e.message) || (es ? 'No se pudo verificar el PIN' : 'Could not verify the PIN');
}

// target: 'primary' (default) | 'spouse' | 'dependent' — selects which
// encrypted SSN the backend should decrypt. Same PIN gate; the audit row
// distinguishes them so the access log keeps each reveal separate. For
// 'dependent', pass `dependentId` (the dependent's stable row id).
//
// NEW-CLIENT mode: when `clientId` is null/undefined AND `editable` is
// true, the client row doesn't exist yet. The modal validates the PIN
// via /auth/verify-pin (audit-logged) and bubbles the typed SSN back
// as `pendingSsn` instead of POSTing — the SSN rides along inside the
// createClient body when the agent saves the whole client. Lissette
// (2026-05-27): "siempre pin aunque sea nuevo" — the PIN gate must
// fire even before the client has an id.
export function SsnRevealModal({ clientId, clientName, onClose, lang, onRevealed, target = 'primary', dependentId, editable = false, onSaved, hasExisting = true }) {
  const es = lang === 'es';
  const isSpouse = target === 'spouse';
  const isDependent = target === 'dependent';
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [revealed, setRevealed] = useState(null); // { ssn, masked }
  const [secondsLeft, setSecondsLeft] = useState(AUTO_HIDE_SECONDS);
  const inputRef = useRef(null);
  // Edit mode: after the PIN reveal the agent can correct a mistyped
  // number right here and save it through the dedicated update-ssn
  // endpoint. We hold onto the PIN they just entered (transient, never
  // cached/persisted) so the save is authorized without a 2nd prompt.
  const [editValue, setEditValue] = useState('');
  const [authedPin, setAuthedPin] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-hide countdown after a successful reveal. When it hits 0 we
  // close the modal so the SSN doesn't linger on screen. Disabled in
  // edit mode — auto-closing while the agent is correcting a number
  // would throw away their edit.
  useEffect(() => {
    if (!revealed || editable) return;
    if (secondsLeft <= 0) { onClose(); return; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [revealed, editable, secondsLeft, onClose]);

  const onlyDigits = (v) => String(v || '').replace(/\D/g, '').slice(0, 6);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(null);
    if (pin.length !== 6) {
      setErr(es ? 'El PIN debe tener 6 dígitos' : 'PIN must be 6 digits');
      return;
    }
    // ADD mode — nothing to decrypt, either because the client row doesn't
    // exist yet (no clientId) or because this person has no SSN on file. Both
    // cases skip the reveal round-trip and drop into an empty editable field.
    //
    // The PIN is checked HERE, against /auth/verify-pin (same bcrypt +
    // lockout + audit shape as reveal-ssn, and it validates a seat against
    // its supervising agent's PIN just like /update-ssn does). Deferring the
    // check to the save was the bug: the agent typed the PIN, typed the nine
    // digits, pressed Guardar and only THEN heard "PIN incorrecto" — with the
    // SSN thrown away and one of her five attempts burned. Five of those and
    // the 15-minute lock closes every SSN door in the CRM. Verify first, and
    // a wrong PIN costs one screen instead of a whole flow (Lissette
    // 2026-08-24: "los social no se están guardando, da error").
    if (editable && (!clientId || !hasExisting)) {
      setSubmitting(true);
      try {
        await verifyPin(pin);
        setRevealed({ ssn: '' });
        setAuthedPin(pin);
        setPin('');
      } catch (e2) {
        setErr(pinErrorText(e2, es));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    try {
      const data = await revealClientSsn(clientId, pin, target, dependentId);
      // Callback mode: hand the plaintext to the caller and close
      // immediately — used by the SSN edit field so the user can modify
      // the value without ever seeing it in this modal.
      if (typeof onRevealed === 'function') {
        onRevealed(data.ssn);
        setPin('');
        onClose();
        return;
      }
      // Inline mode: show the value. In edit mode we also seed the
      // editable field and keep the just-validated PIN so the save
      // doesn't prompt again.
      setRevealed(data);
      if (editable) {
        setEditValue(fmtSsn(data.ssn));
        setAuthedPin(pin);
      }
      setSecondsLeft(AUTO_HIDE_SECONDS);
      setPin('');
    } catch (e2) {
      if (e2.code === 'NO_SSN') {
        // The backend validates the PIN BEFORE checking for ciphertext,
        // so a NO_SSN here means the PIN was correct but nothing is
        // stored yet. In edit mode that's not an error — it's the
        // "add the SSN for the first time" path: drop into the empty
        // editable field, reusing the just-validated PIN to save.
        if (editable) {
          setRevealed({ ssn: '' });
          setEditValue('');
          setAuthedPin(pin);
          setPin('');
        } else {
          setErr(es
            ? (isSpouse ? 'El cónyuge no tiene SSN registrado' : isDependent ? 'El dependiente no tiene SSN registrado' : 'Este cliente no tiene SSN registrado')
            : (isSpouse ? 'Spouse has no SSN on file' : isDependent ? 'Dependent has no SSN on file' : 'This client has no SSN on file'));
        }
      } else {
        setErr(pinErrorText(e2, es));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fmtSsn = (digits) => {
    const d = String(digits || '').replace(/\D/g, '');
    if (d.length !== 9) return d;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  };

  const editDigits = String(editValue || '').replace(/\D/g, '');
  const editValid = editDigits.length === 9;

  const quePin = pinOwnerLabel(es);

  const saveEdit = async () => {
    setErr(null);
    if (!editValid) {
      setErr(es ? 'El SSN debe tener 9 dígitos' : 'SSN must be 9 digits');
      return;
    }
    setSaving(true);
    try {
      // NEW-CLIENT mode: don't POST anywhere. The PIN was already
      // validated against /auth/verify-pin in submit(); now hand the
      // typed SSN back to ClientForm so it rides in the createClient
      // body when the agent saves the whole client. Backend never sees
      // the SSN until the regular POST.
      if (!clientId) {
        const last4 = editDigits.slice(-4);
        const masked = `***-**-${last4}`;
        const result = { target, pendingSsn: editDigits, hasSsn: true, masked, ssnLast4: last4 };
        if (typeof onSaved === 'function') onSaved(result);
        onClose();
        return;
      }
      const result = await updateClientSsn(clientId, authedPin, target, editDigits, dependentId);
      if (typeof onSaved === 'function') onSaved(result);
      onClose();
    } catch (e2) {
      if (e2.code === 'PIN_WRONG' || e2.code === 'PIN_FORMAT' || e2.code === 'PIN_LOCKED') {
        // El PIN ya se valida al abrir, así que llegar aquí significa que
        // caducó entre las dos llamadas (lo rotaron, o el bloqueo saltó por
        // otra pantalla). Se vuelve a pedir el PIN — pero NO se borra el
        // número escrito: perderlo obligaba a teclear los nueve dígitos otra
        // vez y era la mitad de la queja "no se guarda".
        setRevealed(null);
        setAuthedPin('');
        setErr(pinErrorText(e2, es));
      } else if (e2.code === 'DEPENDENT_NOT_FOUND' && isDependent) {
        // The dependent row doesn't exist server-side yet — it was just
        // added to the form and the client hasn't been re-saved, so
        // /update-ssn can't find it by id (404). The PIN was already
        // validated by that same call (the 404 only happens AFTER a
        // correct PIN), so it's safe to stash the typed SSN in form state
        // and let it ride in the next full-client Save — exactly the path
        // a brand-new client uses. Without this, a dependent's SSN could
        // never be saved until the dependent was persisted by a separate
        // save first (Lissette 2026-06-16: "no se guardan los SSN de los
        // dependientes ni cónyuge").
        const last4 = editDigits.slice(-4);
        const result = { target, dependentId, pendingSsn: editDigits, hasSsn: true, masked: `***-**-${last4}`, ssnLast4: last4 };
        if (typeof onSaved === 'function') onSaved(result);
        onClose();
        return;
      } else if (e2.code === 'DEPENDENT_ID_REQUIRED') {
        // The dependent reached the modal without a stable id (legacy row
        // stored without one). Dependents now always get a deterministic id
        // (mapClientFromApi / form init), so this is a safety net: guide the
        // user to persist the dependent first instead of showing the raw
        // backend message. We can't safely stash by an empty id.
        setErr(es
          ? 'Guarda el cliente primero (botón Guardar), reabre la ficha y vuelve a intentar el SSN de este dependiente.'
          : 'Save the client first (Save button), reopen the profile, then try this dependent’s SSN again.');
      } else if (e2.code === 'SSN_FORMAT') {
        setErr(es ? 'El SSN debe tener 9 dígitos' : 'SSN must be 9 digits');
      } else {
        setErr(pinErrorText(e2, es));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={
      (es
        ? (editable ? 'Ver / Editar SSN' : 'Mostrar SSN') + (isSpouse ? ' del cónyuge' : isDependent ? ' del dependiente' : '') + ' — '
        : (editable ? 'View / Edit ' : 'Show ') + (isSpouse ? 'spouse SSN' : isDependent ? 'dependent SSN' : 'SSN') + ' — '
      ) + (clientName || '')
    } onClose={onClose}>
      {!revealed && (
        // Intentionally NOT a <form>. The PIN modal can be opened from
        // inside the ClientForm's <form> (when the agent clicks SHOW on
        // the SSN edit field), and HTML doesn't support nested forms —
        // a button[type=submit] inside the inner "form" was bubbling up
        // and submitting the OUTER ClientForm, which saved the client
        // and dismissed the edit modal. Using a plain div + Enter-key
        // handler + button[type=button] keeps PIN submission scoped.
        <div
          onKeyDown={(e) => { if (e.key === 'Enter' && pin.length === 6 && !submitting) submit(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          <div style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.55, display: 'flex', gap: '.6rem', alignItems: 'flex-start' }}>
            <Lock size={16} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0, marginTop: 2 }} />
            <span>
              {es
                ? (editable
                    ? `Ingresa ${quePin} para ver el SSN. Podrás corregirlo y guardarlo aquí mismo. Cada acceso queda registrado.`
                    : `Ingresa ${quePin} para revelar el SSN completo. Cada acceso queda registrado en el log de actividad.`)
                : (editable
                    ? `Enter ${quePin} to view the SSN. You can correct it and save it right here. Every access is logged.`
                    : `Enter ${quePin} to reveal the full SSN. Every access is recorded in the activity log.`)}
            </span>
          </div>

          <div>
            <label style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>PIN</label>
            <input
              ref={inputRef}
              type="text" inputMode="numeric" autoComplete="off" maxLength={6} data-1p-ignore data-lpignore="true"
              value={pin}
              onChange={(e) => setPin(onlyDigits(e.target.value))}
              placeholder="••••••"
              style={{
                width: '100%', WebkitTextSecurity: 'disc', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
                borderRadius: 8, padding: '12px 14px', color: 'var(--c-white,#eef6ff)',
                fontSize: '1.4rem', fontFamily: 'var(--font-mono)', letterSpacing: '.5em',
                outline: 'none', boxSizing: 'border-box', textAlign: 'center',
              }}
            />
          </div>

          {err && (
            <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ShieldAlert size={14} />
              {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button" onClick={onClose}
              style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem 1rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}
            >{es ? 'Cancelar' : 'Cancel'}</button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || pin.length !== 6}
              style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: pin.length === 6 && !submitting ? 'pointer' : 'not-allowed', opacity: pin.length === 6 && !submitting ? 1 : 0.5 }}
            >{submitting ? (es ? 'Verificando...' : 'Verifying...') : (es ? 'Mostrar SSN' : 'Reveal SSN')}</button>
          </div>
        </div>
      )}

      {revealed && !editable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ fontSize: '.78rem', color: 'var(--c-green,#00ff88)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={14} />
            {es ? 'Acceso autorizado y registrado' : 'Access authorized and logged'}
          </div>
          <div style={{ background: 'var(--c-surface2,#112538)', border: '2px solid var(--c-cyan,#00d4ff)', borderRadius: 12, padding: '1.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.1em', fontFamily: 'var(--font-mono)', marginBottom: '.5rem' }}>SSN</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--c-cyan,#00d4ff)', letterSpacing: '.2em' }}>
              {fmtSsn(revealed.ssn)}
            </div>
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)', textAlign: 'center' }}>
            {es ? 'Se ocultará automáticamente en' : 'Auto-hides in'} <span style={{ color: 'var(--c-amber,#ffb700)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{secondsLeft}s</span>
          </div>
          <button
            type="button" onClick={onClose}
            style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem 1rem', color: 'var(--c-white,#eef6ff)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          ><X size={14} /> {es ? 'Cerrar ahora' : 'Close now'}</button>
        </div>
      )}

      {revealed && editable && (
        <div
          onKeyDown={(e) => { if (e.key === 'Enter' && editValid && !saving) saveEdit(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          <div style={{ fontSize: '.78rem', color: 'var(--c-green,#00ff88)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={14} />
            {revealed && revealed.ssn
              ? (es ? 'Acceso autorizado. Corrige el número si está mal y guarda.' : 'Access authorized. Fix the number if wrong, then save.')
              : (es ? 'PIN verificado. Ingresa el SSN y guarda.' : 'PIN verified. Enter the SSN and save.')}
          </div>
          <div>
            <label style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>SSN</label>
            <input
              type="text" inputMode="numeric" autoComplete="off"
              value={editValue}
              onChange={(e) => {
                const d = e.target.value.replace(/\D/g, '').slice(0, 9);
                setEditValue(d.length > 5 ? `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}` : d);
              }}
              placeholder="123-45-6789"
              style={{
                width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
                borderRadius: 8, padding: '12px 14px', color: 'var(--c-white,#eef6ff)',
                fontSize: '1.4rem', fontFamily: 'var(--font-mono)', letterSpacing: '.15em',
                outline: 'none', boxSizing: 'border-box', textAlign: 'center',
              }}
            />
          </div>

          {err && (
            <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ShieldAlert size={14} />
              {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button" onClick={onClose}
              style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem 1rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}
            >{es ? 'Cerrar' : 'Close'}</button>
            <button
              type="button" onClick={saveEdit}
              disabled={saving || !editValid}
              style={{ background: 'var(--c-green,#00ff88)', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: editValid && !saving ? 'pointer' : 'not-allowed', opacity: editValid && !saving ? 1 : 0.5 }}
            >{saving ? (es ? 'Guardando...' : 'Saving...') : (es ? 'Guardar' : 'Save')}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
